import { useCallback } from "react";
import { useLazyQuery } from "@apollo/client";
import { useMutation } from "@apollo/react-hooks";

import useSuggestedAddresses from "pages/Components/CartAndCheckout/hooks/useSuggestedAddresses";
import useToast from "core/hooks/useToast";
import { FieldList } from "core/hooks/useForm";
import { useAppState } from "core/contexts/AppContext";
import { ADD_SHIPPING_TO_ORDER, getShippingOptions, REMOVE_SHIPPING_FROM_ORDER } from "core/includes/queries";
import useCart from "pages/Components/CartPage/hooks/useCart";
import { getCountryCodeFromName } from "theme/utils";
import { getCanRenderWebp } from "core/includes/image";
import { runPackingProcess } from "core/includes/packing";

type Props = {
    filteredFields: {
        fields: FieldList,
        billingFields: FieldList,
        contactFields: FieldList,
        readOnlyFields: string[]
    },
    usedAddressFinder: boolean
}

const useShipping = ({ filteredFields, usedAddressFinder = false }: Props) => {
    const [, errorToast] = useToast();
    const [appState, setAppState] = useAppState();

    const suggestedAddresses = useSuggestedAddresses();
    const { processCart, getLocalStorageCartUuid } = useCart();

    const [loadShippingOptions, { loading: isShippingLoading }] = useLazyQuery(getShippingOptions);
    const [addShippingToOrder] = useMutation(ADD_SHIPPING_TO_ORDER);
    const [removeShippingFromOrder] = useMutation(REMOVE_SHIPPING_FROM_ORDER);

    /**
     * Gets shipping option based on the passed parameters of the product. These dimensions should be set in the CMS.
     * NZPostAPI is used for NZ customers, ShipptAPI is used for all customers.
     *
     * When we get all the appropriate shipping options for an item, we filter out by the cheapest and that require signature
     * add-on. Since an add-one has its own price, we have to add it separately to the cost. If such shipping exists,
     * then we add it to the cart.
     *
     * If no shipping is found, then a user cannot check out :P
     *
     * @param {number} parcelWeight
     * @param {number} parcelWidth
     * @param {number} parcelHeight
     * @param {number} parcelDepth
     *
     * @return {Promise}
     */
    const getShipping = useCallback(async (packedBoxes: PackedBoxes[]) => {
        let shippingOptions: any = [];
        let suburbOptions: string[] = [];

        if (!appState.cart) {
            return { shippingOptions, suburbOptions };
        }

        // If the street number was not provided from the address search, then the address needs to be updates.
        // Show a toast error message to double-check the address
        if (usedAddressFinder && !filteredFields.contactFields.streetNumber) {
            errorToast('Please check your address.');
            return { shippingOptions, suburbOptions };
        }

        await loadShippingOptions({
            variables: {
                input: JSON.stringify({
                    quote: {
                        "dropoff_address": filteredFields.contactFields.addressOne + ',' + filteredFields.contactFields.addressTwo,
                        "dropoff_postcode": filteredFields.contactFields.postCode,
                        "dropoff_state": filteredFields.contactFields.state,
                        "dropoff_state_short": filteredFields.contactFields.stateShort,
                        "dropoff_suburb": filteredFields.contactFields.city,
                        "dropoff_country_code": getCountryCodeFromName(filteredFields.contactFields.country),
                        "dutiable_amount": appState.cart?.subtotalAmount ?? 0, // for international orders
                        "return_all_quotes": true,
                        "parcel_attributes": packedBoxes.map(packedBox => ({
                            "qty": 1,
                            "weight": packedBox.totalWeight,
                            "width": packedBox.boxType.widthCms,
                            "height": packedBox.boxType.heightCms,
                            "depth": packedBox.boxType.depthCms,
                        }))
                    }
                }),
                orderCountryCode: appState.cart.countryCode
            },
        }).then((res: any) => {
            if (res.error?.graphQLErrors.length) {
                throw new Error(res.error.graphQLErrors[0].debugMessage);
            }

            shippingOptions = res.data?.shippingOptions;
            if (shippingOptions?.length) {
                shippingOptions = [...shippingOptions].sort((a: ShippingOption, b: ShippingOption) => (
                    a.priceExcludingGST - b.priceExcludingGST
                ));
                let shippingAmount = 0;
                let shippingLabels: string[] = [];
                // We get shipping option for each box. Hence we need to calculate how much a customer needs to pay overall
                shippingOptions.forEach((shippingOption: ShippingOption) => {
                    const shippingCost = shippingOption.priceExcludingGST;
                    // CPSR: Courier Post Signature Required
                    const signatureAddon = shippingOption.addons?.filter((addon: ShippingAddons) => addon.addonCode === 'CPSR');
                    shippingAmount += shippingCost + (signatureAddon?.[0]?.priceExcludingGST || 0);
                    shippingLabels.push((shippingOption.niceTitle + " (" + shippingOption.serviceCode + ")"))
                });

                setAppState((prevState: any) => ({
                    cart: {
                        ...prevState.cart,
                        shippingAmount,
                    }
                }));

                addShippingToOrder({
                    variables: {
                        orderUuid: appState.cart.orderUuid,
                        name: shippingLabels.join(', '),
                        amount: shippingAmount,
                        canRenderWebp: getCanRenderWebp()
                    }
                }).then(res => {
                    const cart = res.data.addShippingToOrder;
                    processCart(cart);
                    return cart;
                })
            } else {
                errorToast('Please check your address.');

                // Get the suggested city (suburb) using "Address line 1" & "Postcode" through NZ Post API.
                suggestedAddresses.getSuggestedDetailedAddresses({
                    address: filteredFields.contactFields.addressOne,
                    postCode: filteredFields.contactFields.postCode
                }).then(({ address }) => {
                    if (filteredFields.contactFields.city !== address.city) {
                        suburbOptions = [address.city]
                    }
                });
            }
        }).catch(() => {
            errorToast('Error has occurred. Please contact us.');
        });

        return { shippingOptions, suburbOptions };
    }, [
        appState.cart,
        usedAddressFinder,
        filteredFields.contactFields.streetNumber,
        filteredFields.contactFields.addressOne,
        filteredFields.contactFields.addressTwo,
        filteredFields.contactFields.postCode,
        filteredFields.contactFields.state,
        filteredFields.contactFields.stateShort,
        filteredFields.contactFields.city,
        filteredFields.contactFields.country,
        loadShippingOptions,
        errorToast,
        setAppState,
        addShippingToOrder,
        processCart,
        suggestedAddresses
    ]);

    /**
     * Goes over all available package boxes and tries to pack the products into them. Based on this we find the most
     * appropriate package for the user to use. This will be used to identify the price of shipping later.
     *
     * @return {Promise}
     */
    const packageProducts = useCallback(async () => {
        let shippingOptions: any = [];

        if (!appState.cart) return shippingOptions;

        const packedBoxes: PackedBoxes[] = runPackingProcess(expandItemQuantities(appState.cart?.products));
        await getShipping(packedBoxes)
            .then((res) => shippingOptions = res)
            .catch(() => {
                errorToast('Error has occurred. Please contact us.');
            });

        return shippingOptions;
    }, [appState.cart, errorToast, getShipping]);

    /**
     * Removes the shipping from the order. Mainly for when a customer navigates away. The updated cart is returned.
     *
     * @return {Cart} cart
     */
    const removeShipping = useCallback(() => {
        removeShippingFromOrder({
            variables: {
                orderUuid: appState.cart?.orderUuid || getLocalStorageCartUuid(),
                canRenderWebp: getCanRenderWebp()
            }
        }).then(res => {
            const cart = res.data.removeShippingFromOrder;
            processCart(cart);
            return cart;
        }).catch(() => {
            errorToast('Error has occurred. Please contact us.');
        });
    }, [appState.cart?.orderUuid, errorToast, getLocalStorageCartUuid, processCart, removeShippingFromOrder]);

    return {
        packageProducts,
        removeShipping,
        isShippingLoading
    }
}

export default useShipping;

/**
 * Loops over every product and create a new product based on the original product's quantity. Now we can try packing
 * items separately even if they belong to the same product.
 * @param {ExpandedCartProduct[]} items
 */
export function expandItemQuantities(items: CartProduct[]) {
    const expanded: ExpandedCartProduct[] = [];
    for (const item of items) {
        const qty = item.quantity;
        for (let i = 0; i < qty; i++) {
            expanded.push({
                id: `${item.id}_${i}`,
                productType: item.productType,
                width: item.width,
                height: item.height,
                depth: item.depth,
                weight: item.weight,
                quantity: item.quantity
            });
        }
    }
    return expanded;
}
